import React, { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from "react-router-dom";
import cardChip from "../assets/images/card-chip.svg";
import iconTrash from "../assets/images/trash-gif.gif";
import { useSnackbar } from 'notistack';
import { cancelPlan, getBillingMe, getPlans } from '../shared/_services/billing.services';
import StarsIcon from "../styles/StarsIcon";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Panel,
} from "./ui";

import { PLAN_CARD_PATH, PLAN_PATH, useBillingResult } from "./plan/planNav";
import {
    APPLE_BILLING_NOTE,
    CANCEL_BODY,
    CANCEL_CONFIRM,
    CANCEL_KEEP,
    CANCEL_TITLE,
    CANCELLED_NO_CHANGE,
    FIRST_CHARGE_ON,
    MANAGE_IN_APP_STORE,
    PLAN_ENDS_ON,
    STRIPE_BILLING_NOTE,
} from "./plan/planCopy";
import { LINKED_ACCOUNTS_PATH } from "./plaid/plaidNav";
import { LINKED_TITLE } from "./plaid/plaidCopy";

/** "$38.50" / "$99" from a dollar amount; null when there is none. */
export function formatPlanAmount(amount) {
  if (amount == null) return null;
  return `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
}

// `card` is GET /billing/me's display object: {brand, last4, expMonth,
// expYear} — camelCase, not Stripe's exp_month/exp_year. Exported for tests.
export function formatCardExpiry(card) {
  if (card?.expMonth == null || card?.expYear == null) return null;
  const month = String(card.expMonth).padStart(2, "0");
  const year = String(card.expYear).slice(-2);
  return `${month}/${year}`;
}

// Pooled inbox SP5 (spec § Screens per app → Web, /profile): the legacy panel
// keeps its look; its data is GET /billing/me (the entitlement, the card, the
// App Store link) with the price from GET /billing/plans, and its actions are
// /billing/*: Cancel at period end, Change plan → /plan (refused once
// cancelled, owner item 17), Add New Card → /plan/card. A free-month client
// sees the first-charge date (owner item 18). An App Store member manages the
// plan there; a comped client (source admin) has nothing to cancel or pay.
function Subscription({ handleCancelStatus }) {
    const { enqueueSnackbar } = useSnackbar();
    const settle = useBillingResult();
    // The actions stay disabled until the summary has loaded: treating "not
    // loaded yet" or "failed" as "not cancelled" would offer a cancelled
    // client the cancel dialog and plan-change navigation.
    const [me, setMe] = useState(null);
    const [plans, setPlans] = useState([]);
    const [detailsState, setDetailsState] = useState('loading'); // 'loading' | 'loaded' | 'error'
    const navigate = useNavigate();
    const [iscancelled, setIsCancelled] = useState(false);
    const inFlight = useRef(false);

    //   popup functions
    const [cancelState, setCancelState] = useState(false);
    const closeModal = () => setCancelState(false);

    const fetchSubscriptionDetails = async () => {
        setDetailsState('loading');
        try {
            const [mine, list] = await Promise.all([getBillingMe(), getPlans()]);
            setMe(mine.data);
            setPlans(list.data.plans);
            setDetailsState('loaded');
        } catch (_) {
            setDetailsState('error');
        }
    }

    useEffect(() => {
        fetchSubscriptionDetails();
    }, []); // eslint-disable-line

    const entitlement = me?.entitlement;
    const card = me?.card;
    const plan = plans.find((p) =>
        entitlement?.tier ? p.tier === entitlement.tier : p.name === entitlement?.planName
    );
    const detailsReady = detailsState === 'loaded';
    const cancellationRequested = entitlement?.cancelAtPeriodEnd === true;
    const stripeBilled = entitlement?.source === 'stripe';
    const appleBilled = entitlement?.source === 'apple';
    const dateLine = cancellationRequested && entitlement?.currentPeriodEnd
        ? PLAN_ENDS_ON(entitlement.currentPeriodEnd)
        : entitlement?.state === 'trialing' && entitlement?.trialEndsAt
            ? FIRST_CHARGE_ON(entitlement.trialEndsAt)
            : null;

    const openModal = () => setCancelState(true);

    const handleChangePlan = () => {
        if (!cancellationRequested) {
            navigate(PLAN_PATH);
        }
        else {
            enqueueSnackbar(CANCELLED_NO_CHANGE, {
                variant: 'error',
                anchorOrigin: { horizontal: 'right', vertical: 'top' },
            });
        }
    };

    const handleAddNewCard = () => navigate(PLAN_CARD_PATH);

    const handleCancelSubscription = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setIsCancelled(true);
        try {
            const res = await cancelPlan();
            await settle(res.data.entitlement);
            handleCancelStatus?.(true);
            closeModal();
            await fetchSubscriptionDetails();
            enqueueSnackbar("Subscription cancelled successfully.", {
                variant: 'error',
                anchorOrigin: { horizontal: 'right', vertical: 'top' },
            });
        } catch (err) {
            inFlight.current = false;
            closeModal();
            enqueueSnackbar(err?.response?.data?.message || "Unable to cancel. Try again.", {
                variant: 'error',
                anchorOrigin: { horizontal: 'right', vertical: 'top' },
            });
        }
        setIsCancelled(false);
    }

    const stateTone = cancellationRequested ? 'warning' : 'neutral';

    return (
        <div className="tw-grid tw-gap-6">
            <Panel title="Subscription">
                <div>
                    <div className="tw-flex tw-items-baseline tw-gap-2">
                        {!detailsReady && detailsState !== 'error' ? (
                            <span className="tw-block tw-h-8 tw-w-24 tw-animate-pulse tw-rounded-sm tw-bg-surface-sunken"></span>
                        ) : (
                            <span className="tw-text-2xl tw-font-bold tw-tabular-nums tw-text-text">
                                {formatPlanAmount(plan ? plan.priceCents / 100 : null) ?? "—"}
                            </span>
                        )}
                        {plan && (
                            <span className="tw-text-sm tw-text-text-muted">/ Month</span>
                        )}
                    </div>
                    <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-text-muted">
                        {entitlement?.planName ? `${entitlement.planName} Plan` : ""}
                    </p>
                    {dateLine && (
                        <div className="tw-mt-2">
                            <Badge tone={stateTone} className="tw-tabular-nums">{dateLine}</Badge>
                        </div>
                    )}
                </div>
                <div className="tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    {appleBilled && me?.manageUrl && (
                        <Button
                            as="a"
                            variant="quiet"
                            href={me.manageUrl}
                            target="_blank"
                            rel="noreferrer"
                        >
                            {MANAGE_IN_APP_STORE}
                        </Button>
                    )}
                    {stripeBilled && (
                        <Button
                            variant={cancellationRequested ? 'quiet' : 'danger'}
                            onClick={cancellationRequested ? undefined : openModal}
                            disabled={!detailsReady}
                            aria-disabled={cancellationRequested ? 'true' : undefined}
                        >
                            {cancellationRequested
                                ? "Subscription Cancelled"
                                : "Cancel Subscription"}
                        </Button>
                    )}

                    {stripeBilled && (
                        <Button
                            variant={cancellationRequested ? 'primary' : 'ghost'}
                            onClick={handleChangePlan}
                            disabled={!detailsReady}
                        >
                            Change Plan
                        </Button>
                    )}
                </div>
            </Panel>
            <Panel title="Payment Method">
                {stripeBilled && card && (
                    <div className="stars-dark tw-grid tw-max-w-sm tw-gap-4 tw-rounded-pass tw-bg-material-blue tw-bg-cover tw-bg-center tw-p-6 tw-text-text">
                        <div className="tw-flex tw-items-center tw-justify-between">
                            <img width={30} src={cardChip} alt="" />
                            <span className="tw-text-md tw-font-bold tw-capitalize">
                                {card.brand}
                            </span>
                        </div>
                        <div className="tw-flex tw-gap-3 tw-text-lg tw-font-semibold tw-tracking-wider tw-tabular-nums">
                            <span>****</span>
                            <span>****</span>
                            <span>****</span>
                            <span>{card.last4}</span>
                        </div>
                        <div>
                            <p className="tw-m-0 tw-text-xs tw-text-text-muted">Expiry Date</p>
                            <p className="tw-m-0 tw-text-md tw-font-semibold tw-tabular-nums tw-text-text">{formatCardExpiry(card)}</p>
                        </div>
                    </div>
                )}
                {stripeBilled && (
                    <p className="tw-mb-0 tw-mt-4 tw-text-sm tw-text-text-muted">{STRIPE_BILLING_NOTE}</p>
                )}
                {appleBilled && (
                    <p className="tw-m-0 tw-text-sm tw-text-text-muted">{APPLE_BILLING_NOTE}</p>
                )}
                <div className="tw-mt-6 tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                    <Button as={Link} variant="quiet" to={LINKED_ACCOUNTS_PATH}>
                        {LINKED_TITLE}
                    </Button>
                    {stripeBilled && (
                        <Button variant="ghost" onClick={handleAddNewCard}>
                            <StarsIcon name="plus" className="tw-h-4 tw-w-4 tw-shrink-0" />
                            Add New Card
                        </Button>
                    )}
                </div>
            </Panel>

            {/* cancel subscription popup */}
            <Dialog
                open={cancelState}
                onOpenChange={(open) => { if (!open) closeModal(); }}
            >
                <DialogContent
                    aria-describedby="cancelSubscriptionBody"
                    onInteractOutside={(e) => e.preventDefault()}
                    onEscapeKeyDown={(e) => e.preventDefault()}
                >
                    <div className="tw-flex tw-justify-end">
                        <Button
                            variant="icon"
                            size="sm"
                            iconOnly
                            aria-label="Close"
                            onClick={closeModal}
                        >
                            <StarsIcon name="x" className="tw-h-4 tw-w-4 tw-shrink-0" />
                        </Button>
                    </div>
                    <div className="tw-flex tw-justify-center">
                        <img width={43} src={iconTrash} alt="trash" />
                    </div>
                    <DialogTitle className="tw-mt-4 tw-text-center">{CANCEL_TITLE}</DialogTitle>
                    <DialogDescription id="cancelSubscriptionBody" className="tw-text-center">{CANCEL_BODY}</DialogDescription>
                    <DialogActions className="tw-justify-center">
                        <Button variant="quiet" onClick={closeModal}>
                            {CANCEL_KEEP}
                        </Button>
                        <Button onClick={handleCancelSubscription} disabled={iscancelled}>
                            <StarsIcon name="check" className="tw-h-4 tw-w-4 tw-shrink-0" />
                            {iscancelled ? "Cancelling.." : CANCEL_CONFIRM}
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </div>
    )
}

export default Subscription
